// Utils
import { getCanAdministerShares } from 'modules/shares/utils'

// Types
import {
  GetBillingHistoryCountQuery,
  GetCustomerProviderQuery,
  GetWorkspaceSettingsQuery,
  WorkspaceMinimalFragment,
  GetCustomerQuery,
} from '@sketch/gql-types'

interface GetPermissionsArgs {
  billingHistoryCount?: GetBillingHistoryCountQuery
  customerProvider?: GetCustomerProviderQuery
  settings?: GetWorkspaceSettingsQuery['workspace']
  workspace: WorkspaceMinimalFragment
  customer?: GetCustomerQuery['customer']
}

export const getWorkspaceUserRolePermissions = (
  workspace: WorkspaceMinimalFragment
) => {
  const isFinance = workspace.userRole === 'FINANCE'
  const isPartner = workspace.userRole === 'PARTNER'
  const isAdmin = workspace.userRole === 'ADMIN'
  const isGuest = workspace.userRole === 'GUEST'

  const canManageBillingSetting = isAdmin || isFinance || isPartner

  return {
    isFinance,
    isPartner,
    isGuest,
    isAdmin,
    canManageBillingSetting,
  }
}

export const getWorkspaceTypePermissions = (
  workspace: WorkspaceMinimalFragment
) => {
  const isPersonalWorkspace = workspace.type === 'PERSONAL'
  const isEducationWorkspace = workspace.type === 'STUDENT'

  return { isPersonalWorkspace, isEducationWorkspace }
}

export const getWorkspaceSettingsSSOPermissions = (
  settings?: GetWorkspaceSettingsQuery['workspace']
) => {
  return !!settings?.features?.ssoEnabled
}

export const getWorkspaceCustomerIOSPermissions = (
  customerProvider?: GetCustomerProviderQuery
) => {
  const isIOS =
    customerProvider?.customer?.subscription?.provider === 'REVENUECAT'

  return isIOS
}

export const getWorkspaceCustomerHasInvoices = (
  billingHistoryCount?: GetBillingHistoryCountQuery
) => {
  const hasInvoices = !!billingHistoryCount?.customer?.billingHistory.meta
    .totalCount
  return hasInvoices
}

/**
 *
 * Aggregates a bunch of workspace permissions in a tidy object
 * to make it easier to use
 */
const workspaceSettingsPermissions = ({
  workspace,
  customerProvider,
  billingHistoryCount,
  settings,
  customer,
}: GetPermissionsArgs) => {
  const {
    isFinance,
    isGuest,
    isAdmin,
    isPartner,
    canManageBillingSetting,
  } = getWorkspaceUserRolePermissions(workspace)
  const {
    isEducationWorkspace,
    isPersonalWorkspace,
  } = getWorkspaceTypePermissions(workspace)

  const isIOS = getWorkspaceCustomerIOSPermissions(customerProvider)
  const hasInvoices = getWorkspaceCustomerHasInvoices(billingHistoryCount)

  const isAllowedBillingRouteForEducationWorkspace = !isEducationWorkspace
    ? true
    : hasInvoices

  const showWorkspaceSettingsGeneral =
    !isPersonalWorkspace &&
    isAdmin &&
    !isFinance &&
    !isIOS &&
    !isEducationWorkspace

  const showWorkspaceSettingsBilling =
    !isPersonalWorkspace &&
    canManageBillingSetting &&
    isAllowedBillingRouteForEducationWorkspace &&
    !isIOS

  const showWorkspaceSettingsSSO = !isPersonalWorkspace && isAdmin && !isIOS

  const hideNavigation =
    isIOS ||
    (isEducationWorkspace && !isAllowedBillingRouteForEducationWorkspace)

  const showDiscover = !(isFinance || isPartner)

  const showUpsell =
    isAdmin && customer?.subscription?.currentPlan?.product === 'TEAMS'

  return {
    hideNavigation,
    isAdmin,
    isEducationWorkspace,
    isFinance,
    isGuest,
    isIOS,
    isPartner,
    isPersonalWorkspace,
    showDiscover,
    showUpsell,
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    showWorkspaceSettingsSSO,
  }
}

const showWorkspaceSettings = (workspace: WorkspaceMinimalFragment): boolean =>
  workspace.userRole !== 'GUEST'

const canUserAdministerShares = (
  workspace: WorkspaceMinimalFragment
): boolean => getCanAdministerShares(workspace)

export {
  showWorkspaceSettings,
  canUserAdministerShares,
  workspaceSettingsPermissions,
}
